import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import FormDialog from "../common/FormDialog";
import FormField from "../common/FormField";
import useFormValidation from "../../hooks/useFormValidation";
import {
  email,
  mobile,
  required,
  sanitizeNationalPhone,
} from "../../utils/formValidation";
import { tenantmgmtService } from "../../services";
import { appActions } from "../../redux/actions";
import { getApiErrorMessage } from "../../helpers";

const emptyForm = {
  name: "",
  email: "",
  contactPerson: "",
  mobile: "",
};

const tenantSchema = {
  name: [required("Tenant name is required.")],
  email: [required("Email is required."), email()],
  contactPerson: [required("Contact person is required.")],
  mobile: [mobile()],
};

const getTenantId = (tenant) => tenant?.id || tenant?._id || tenant?.originalId || "";

const CreateTenant = ({ open, onClose, tenantData, onSuccess }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState("");
  const isEdit = Boolean(tenantData);

  const initialValues = useMemo(() => {
    if (!tenantData) return emptyForm;
    return {
      name: tenantData.name || "",
      email: tenantData.email || "",
      contactPerson: tenantData.contactPerson || "",
      mobile: sanitizeNationalPhone(tenantData.mobile || ""),
    };
  }, [tenantData]);

  const { values, getFieldProps, validateAll, reset, setFieldValue } =
    useFormValidation(initialValues, tenantSchema);

  useEffect(() => {
    if (!open) return;
    setApiError("");
    setLoading(false);
    reset(initialValues);
  }, [open, initialValues, reset]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setApiError(
        errors.name ||
          errors.email ||
          errors.contactPerson ||
          errors.mobile ||
          "Please fill in all required fields correctly."
      );
      return;
    }

    const payload = {
      name: values.name.trim(),
      email: values.email.trim(),
      contactPerson: values.contactPerson.trim(),
      mobile: sanitizeNationalPhone(values.mobile),
    };

    setApiError("");
    setLoading(true);
    try {
      if (isEdit) {
        const tenantId = getTenantId(tenantData);
        if (!tenantId) {
          setApiError("Tenant ID is missing.");
          return;
        }
        await tenantmgmtService.updateTenant(tenantId, payload);
        dispatch(appActions.apiSuccessMessage("Tenant updated successfully."));
      } else {
        await tenantmgmtService.createTenant(payload);
        dispatch(appActions.apiSuccessMessage("Tenant created successfully."));
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setApiError(
        getApiErrorMessage(err) ||
          (isEdit ? "Failed to update tenant." : "Failed to add tenant.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={isEdit ? "Edit Tenant" : "Add Tenant"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={apiError}
      onClearError={() => setApiError("")}
      submitLabel={isEdit ? "Update Tenant" : "Add Tenant"}
      loadingLabel={isEdit ? "Updating..." : "Creating..."}
    >
      <FormField label="Tenant Name" required {...getFieldProps("name")} />
      <FormField label="Email" type="email" required {...getFieldProps("email")} />
      <FormField label="Contact Person" required {...getFieldProps("contactPerson")} />
      <FormField
        label="Mobile"
        {...getFieldProps("mobile")}
        onChange={(event) => {
          setFieldValue("mobile", sanitizeNationalPhone(event.target.value));
        }}
        inputProps={{ maxLength: 10, inputMode: "numeric" }}
      />
    </FormDialog>
  );
};

export default CreateTenant;
